import {
    useEffect,
    useRef,
    type MouseEvent,
    type PointerEvent,
    type RefObject,
} from "react";
// The nav runs no View Transitions; a snap must land in the frame that re-bases the stretch.
// react-doctor-disable-next-line react-doctor/no-flush-sync
import { flushSync } from "react-dom";
import type { SideNavMode } from "./SideNav.types";
import { toDevicePixels, velocityOf } from "./sideNavDragMath";
import {
    applyStretch,
    clearStretch,
    DIRECTION_TO,
    isClickRelease,
    isOnHeldSpot,
    isResizing,
    OTHER_MODE,
    startDragSession,
    stretchAt,
    type DragSession,
} from "./sideNavDragSession";
import {
    clampOpenWidth,
    DEFAULT_OPEN_WIDTH,
    MIN_OPEN_WIDTH,
} from "./sideNavWidth";

/** Pointer travel before the nav starts following the pointer. */
const CLICK_SLOP_PX = 3;
/** Travel back past the snap point before snapping back, so jitter there doesn't flicker. */
const SNAP_HYSTERESIS_PX = 4;
/** Release speed toward the other width that snaps short of the snap point. */
const FLICK_PX_PER_MS = 0.11;
const VELOCITY_WINDOW_MS = 100;
/** How long after a click its second click still counts as a double-click (macOS's default). */
const DOUBLE_CLICK_MS = 500;

/**
 * Drag handlers for the nav's right edge. Open, the edge follows the pointer
 * between 180px and where every label fits (at most 400px), rubber-banding
 * past either; 48px past 180px it snaps to mini. The release applies the nearest
 * width in range, or snaps to mini if it's a flick below 180px. From mini it
 * snaps open, to the open width, 48px along, rubber-banding without resizing,
 * and springs back short of that unless it's a flick. A mini nav dragged within
 * 28px of its left edge slides off and hides on release. A press released within
 * 8px, without snapping, stays a click and toggles; a double-click resets the width.
 */
export const useSideNavEdgeDrag = ({
    mode,
    setMode,
    toggleMode,
    setHidden,
    width,
    setWidth,
    navRef,
}: {
    mode: SideNavMode;
    setMode: (mode: SideNavMode) => void;
    toggleMode: () => void;
    setHidden: (isHidden: boolean) => void;
    width: number;
    setWidth: (width: number) => void;
    navRef: RefObject<HTMLElement | null>;
}) => {
    const sessionRef = useRef<DragSession | null>(null);
    const isClickSuppressedRef = useRef(false);
    const pressedSpotTimerRef =
        useRef<ReturnType<typeof setTimeout>>(undefined);

    useEffect(() => {
        const nav = navRef.current;
        return () => {
            clearTimeout(pressedSpotTimerRef.current);
            if (nav !== null) {
                clearStretch(nav);
            }
        };
    }, [navRef]);

    const sessionFor = (event: PointerEvent<HTMLElement>) => {
        const session = sessionRef.current;
        return session?.pointerId === event.pointerId ? session : null;
    };

    const snapTo = (session: DragSession, nextMode: SideNavMode) => {
        session.mode = nextMode;
        session.stretch = 0;
        // Transitions on and stretch cleared before the width changes, so it
        // animates from where it is instead of jumping to the new width minus the old stretch.
        if (navRef.current !== null) {
            applyStretch({
                nav: navRef.current,
                stretch: 0,
                phase: "snapping",
            });
        }
        flushSync(() => setMode(nextMode));
    };

    const track = (session: DragSession, event: PointerEvent<HTMLElement>) => {
        const x = event.clientX;
        const time = event.timeStamp;
        session.samples = [...session.samples, { x, time }].filter(
            (sample) => time - sample.time <= VELOCITY_WINDOW_MS,
        );
        session.maxTravel = Math.max(
            session.maxTravel,
            Math.abs(x - session.startX),
        );
        if (!session.isDragging && session.maxTravel < CLICK_SLOP_PX) {
            return;
        }
        session.isDragging = true;

        const otherMode = OTHER_MODE[session.startMode];
        const travelPastSnap = (x - session.snapX) * DIRECTION_TO[otherMode];
        const snapBackAt = session.mode === otherMode ? -SNAP_HYSTERESIS_PX : 0;
        const pointerMode =
            travelPastSnap >= snapBackAt ? otherMode : session.startMode;
        if (pointerMode !== session.mode) {
            snapTo(session, pointerMode);
            session.snappedAt = time;
            session.originX =
                pointerMode === session.startMode ? session.startX : x;
        }

        const nav = navRef.current;
        if (nav === null) {
            return;
        }
        const isHiding =
            session.mode === "mini" &&
            x - session.hideX < (session.isHiding ? SNAP_HYSTERESIS_PX : 0);
        if (isHiding !== session.isHiding) {
            session.isHiding = isHiding;
            session.slidAt = time;
        }
        if (isHiding) {
            applyStretch({ nav, stretch: 0, phase: "hiding" });
            return;
        }
        const isSnapping = time - session.snappedAt < session.snapMs;
        const isSliding = time - session.slidAt < session.snapMs;
        session.stretch = toDevicePixels(stretchAt({ session, x, isSnapping }));
        applyStretch({
            nav,
            stretch: session.stretch,
            phase: isSnapping || isSliding ? "snapping" : "tracking",
        });
    };

    const end = () => {
        sessionRef.current = null;
        if (navRef.current !== null) {
            clearStretch(navRef.current);
        }
    };

    const onPointerDown = (event: PointerEvent<HTMLElement>) => {
        if (sessionRef.current !== null || event.button !== 0) {
            return;
        }
        // Without this the press starts a text selection, and Chrome turns it
        // into a native drag of the page beside the edge, cancelling the pointer.
        event.preventDefault();
        isClickSuppressedRef.current = false;
        if (isOnHeldSpot(event)) {
            return;
        }
        event.currentTarget.setPointerCapture(event.pointerId);
        sessionRef.current = startDragSession({
            pointerId: event.pointerId,
            mode,
            width,
            startX: event.clientX,
            time: event.timeStamp,
            nav: navRef.current,
        });
    };

    const onPointerMove = (event: PointerEvent<HTMLElement>) => {
        const session = sessionFor(event);
        if (session !== null) {
            track(session, event);
        }
    };

    const onPointerUp = (event: PointerEvent<HTMLElement>) => {
        const session = sessionFor(event);
        if (session === null) {
            return;
        }
        track(session, event);
        const otherMode = OTHER_MODE[session.mode];
        const pointerWidth = event.clientX - session.edgeX;
        const isFlick =
            velocityOf(session.samples) * DIRECTION_TO[otherMode] >
            FLICK_PX_PER_MS;
        // From open, only a flick below the minimum snaps, so a quick resize doesn't collapse it.
        const isFlickSnap =
            session.isDragging &&
            isFlick &&
            (session.mode === "mini" || pointerWidth < MIN_OPEN_WIDTH);
        if (session.isHiding) {
            // In the frame that drops `data-edge-drag`, so `data-hidden` takes over the slide.
            flushSync(() => setHidden(true));
        } else if (isFlickSnap) {
            snapTo(session, otherMode);
        } else if (isResizing(session) && !isClickRelease(session)) {
            const nextWidth = toDevicePixels(
                clampOpenWidth({
                    width: pointerWidth,
                    max: session.fittedWidth,
                }),
            );
            // The stretch drops in the same commit that gives the nav its new
            // width. A layout read in between (an effect, React DevTools)
            // would otherwise see the new width plus the old stretch, and the
            // transition would start there.
            const nav = navRef.current;
            if (nav !== null) {
                clearStretch(nav);
            }
            flushSync(() => setWidth(nextWidth));
        }
        isClickSuppressedRef.current = !isClickRelease(session);
        end();
    };

    /** Cancel and lost capture spring back, like a slow release. */
    const onPointerCancel = (event: PointerEvent<HTMLElement>) => {
        if (sessionFor(event) !== null) {
            end();
        }
    };

    /**
     * The toggle moves the edge out from under the pointer, so the handle
     * keeps the pressed spot for a double-click's length and the second click
     * still lands on it.
     */
    const holdPressedSpot = (event: MouseEvent<HTMLElement>) => {
        const handle = event.currentTarget;
        handle.style.setProperty("--side-nav-press-x", `${event.clientX}px`);
        handle.style.setProperty("--side-nav-press-y", `${event.clientY}px`);
        handle.setAttribute("data-press-held", "");
        clearTimeout(pressedSpotTimerRef.current);
        pressedSpotTimerRef.current = setTimeout(
            () => handle.removeAttribute("data-press-held"),
            DOUBLE_CLICK_MS,
        );
    };

    const releasePressedSpot = (handle: HTMLElement) => {
        clearTimeout(pressedSpotTimerRef.current);
        handle.removeAttribute("data-press-held");
    };

    /** A click toggles; the second click of a double-click resets the open width instead. */
    const onClick = (event: MouseEvent<HTMLElement>) => {
        if (isClickSuppressedRef.current) {
            isClickSuppressedRef.current = false;
            return;
        }
        if (event.detail === 2) {
            releasePressedSpot(event.currentTarget);
            setMode("open");
            setWidth(DEFAULT_OPEN_WIDTH);
        } else if (isOnHeldSpot(event)) {
            releasePressedSpot(event.currentTarget);
        } else if (event.detail <= 1) {
            toggleMode();
            if (event.detail === 1) {
                holdPressedSpot(event);
            }
        }
    };

    return {
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel,
        onLostPointerCapture: onPointerCancel,
        onClick,
    };
};
